import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, BarChart3, TrendingUp } from 'lucide-react';
import { featuresAPI } from '../utils/api';

const PeerComparison = ({ roadmaps = [], analytics = null }) => {
    const [apiData, setApiData] = useState(null);

    useEffect(() => {
        const fetchPeers = async () => {
            try {
                const result = await featuresAPI.getPeerComparison();
                if (result) setApiData(result);
            } catch (err) {
                // Use local fallback
            }
        };
        fetchPeers();
    }, []);

    const comparisonData = useMemo(() => {
        // Prefer API data
        if (apiData) {
            const activeRoadmap = roadmaps.find(r => r.isActive) || roadmaps[0];
            const progress = activeRoadmap?.progress?.progressPercentage || 0;
            return {
                peerChoice: apiData.peer_choice || apiData.peerChoice || 73,
                avgCompletion: apiData.avg_completion || apiData.avgCompletion || 42,
                topCareer: apiData.top_career || apiData.topCareer || 'Data Science',
                alternatives: apiData.popular_paths || apiData.alternatives || [],
                percentile: apiData.percentile || 50,
                progress,
                careerPath: activeRoadmap?.careerPath || apiData.career_path || 'Your Path',
            };
        }

        if (!roadmaps.length) return null;

        const activeRoadmap = roadmaps.find(r => r.isActive) || roadmaps[0];
        const careerPath = activeRoadmap?.careerPath?.toLowerCase() || '';
        const progress = activeRoadmap?.progress?.progressPercentage || 0;

        // Simulated peer data based on career path
        let peerChoice = 73;
        let avgCompletion = 42;
        let topCareer = 'Data Science';
        let alternatives = [];

        if (careerPath.includes('software') || careerPath.includes('developer')) {
            peerChoice = 78;
            avgCompletion = 38;
            topCareer = 'Full Stack Development';
            alternatives = [
                { path: 'Backend Engineering', percentage: 78 },
                { path: 'Frontend Development', percentage: 65 },
                { path: 'DevOps Engineering', percentage: 45 },
                { path: 'Mobile Development', percentage: 32 },
            ];
        } else if (careerPath.includes('data') || careerPath.includes('ml') || careerPath.includes('ai')) {
            peerChoice = 72;
            avgCompletion = 35;
            topCareer = 'Data Science';
            alternatives = [
                { path: 'ML Engineering', percentage: 72 },
                { path: 'Data Engineering', percentage: 58 },
                { path: 'AI Research', percentage: 38 },
                { path: 'Business Analytics', percentage: 28 },
            ];
        } else if (careerPath.includes('design') || careerPath.includes('ux')) {
            peerChoice = 68;
            avgCompletion = 44;
            topCareer = 'Product Design';
            alternatives = [
                { path: 'UX Design', percentage: 68 },
                { path: 'UI Engineering', percentage: 52 },
                { path: 'Product Management', percentage: 42 },
                { path: 'Design Systems', percentage: 25 },
            ];
        } else {
            alternatives = [
                { path: 'Software Engineering', percentage: 65 },
                { path: 'Data Science', percentage: 55 },
                { path: 'Product Management', percentage: 40 },
                { path: 'UX Design', percentage: 30 },
            ];
        }

        const percentile = progress > avgCompletion
            ? Math.min(95, Math.round(50 + (progress - avgCompletion) * 1.5))
            : Math.max(20, Math.round(50 - (avgCompletion - progress) * 1.2));

        return {
            peerChoice,
            avgCompletion,
            topCareer,
            alternatives,
            percentile,
            progress,
            careerPath: activeRoadmap?.careerPath || 'Your Path'
        };
    }, [roadmaps, analytics, apiData]);

    if (!comparisonData) return null;

    return (
        <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
                <Users className="w-5 h-5 text-blue-400" />
                <h3 className="font-bold text-white text-sm">What Others Like You Chose</h3>
            </div>

            {/* Main stat */}
            <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-4 mb-5 text-center">
                <div className="text-3xl font-bold text-blue-400 mb-1">{comparisonData.peerChoice}%</div>
                <p className="text-xs text-slate-400">
                    of users with similar skills chose <span className="text-white font-medium">{comparisonData.topCareer}</span>
                </p>
            </div>

            {/* Your standing */}
            <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-xl p-3 mb-5">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-slate-400">Your standing among peers</span>
                    <span className="text-xs font-mono text-emerald-400">Top {100 - comparisonData.percentile}%</span>
                </div>
                <div className="relative h-2 bg-slate-800 rounded-full overflow-hidden">
                    <motion.div
                        className="absolute h-full bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${comparisonData.percentile}%` }}
                        transition={{ duration: 1, delay: 0.3 }}
                    />
                    {/* Marker */}
                    <motion.div
                        className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border-2 border-emerald-500 shadow-lg"
                        initial={{ left: '0%' }}
                        animate={{ left: `${comparisonData.percentile}%` }}
                        transition={{ duration: 1, delay: 0.3 }}
                        style={{ marginLeft: '-6px' }}
                    />
                </div>
                <div className="flex justify-between text-[10px] text-slate-600 font-mono mt-1">
                    <span>Avg peer: {comparisonData.avgCompletion}%</span>
                    <span>You: {comparisonData.progress}%</span>
                </div>
            </div>

            {/* Alternative paths */}
            <div>
                <h4 className="text-[10px] font-mono text-slate-500 uppercase mb-3">Popular Paths for Similar Profiles</h4>
                <div className="space-y-2">
                    {comparisonData.alternatives.map((alt, i) => (
                        <div key={i} className="flex items-center gap-3">
                            <span className="text-xs text-slate-400 w-32 truncate">{alt.path}</span>
                            <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                <motion.div
                                    className="h-full rounded-full bg-blue-500/60"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${alt.percentage}%` }}
                                    transition={{ duration: 0.8, delay: 0.1 * i }}
                                />
                            </div>
                            <span className="text-xs font-mono text-blue-400 w-8 text-right">{alt.percentage}%</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default PeerComparison;
